import { useCallback, useEffect, useRef, useState } from "react";

interface UseTimesheetLeaveProtectionParams {
  hasPendingChanges: boolean;
  onSave: () => Promise<void>;
  onDiscard: () => void;
}

export interface UseTimesheetLeaveProtectionReturn {
  modalOpen: boolean;
  isNavigatingAway: React.MutableRefObject<boolean>;
  isSaving: boolean;
  handleSaveAndLeave: () => void;
  handleLeaveWithout: () => void;
  handleStay: () => void;
}

export function useTimesheetLeaveProtection({
  hasPendingChanges,
  onSave,
  onDiscard,
}: UseTimesheetLeaveProtectionParams): UseTimesheetLeaveProtectionReturn {
  const [modalOpen, setModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const pendingActionRef = useRef<(() => void) | null>(null);
  const isNavigatingAway = useRef(false);

  const requestConfirmation = useCallback(
    (action: () => void) => {
      if (!hasPendingChanges) {
        action();
        return;
      }
      pendingActionRef.current = action;
      setModalOpen(true);
    },
    [hasPendingChanges],
  );

  function handleSaveAndLeave() {
    isNavigatingAway.current = true;
    setIsSaving(true);
    onSave()
      .then(() => {
        setModalOpen(false);
        setIsSaving(false);
        if (pendingActionRef.current) {
          pendingActionRef.current();
          pendingActionRef.current = null;
        }
      })
      .catch(() => {
        isNavigatingAway.current = false;
        setIsSaving(false);
        setModalOpen(false);
        pendingActionRef.current = null;
      });
  }

  function handleLeaveWithout() {
    onDiscard();
    setModalOpen(false);
    if (pendingActionRef.current) {
      pendingActionRef.current();
      pendingActionRef.current = null;
    }
  }

  function handleStay() {
    pendingActionRef.current = null;
    setModalOpen(false);
  }

  useEffect(() => {
    function handler(e: BeforeUnloadEvent) {
      if (!hasPendingChanges) return;
      e.preventDefault();
      e.returnValue = "";
    }
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasPendingChanges]);

  useEffect(() => {
    const originalPush = window.history.pushState;
    const originalReplace = window.history.replaceState;

    window.history.pushState = (...args: Parameters<typeof window.history.pushState>) => {
      if (hasPendingChanges) {
        requestConfirmation(() => {
          originalPush.apply(window.history, args);
        });
        return;
      }
      originalPush.apply(window.history, args);
    };

    window.history.replaceState = (...args: Parameters<typeof window.history.replaceState>) => {
      if (hasPendingChanges) {
        requestConfirmation(() => {
          originalReplace.apply(window.history, args);
        });
        return;
      }
      originalReplace.apply(window.history, args);
    };

    return () => {
      window.history.pushState = originalPush;
      window.history.replaceState = originalReplace;
    };
  }, [hasPendingChanges, requestConfirmation]);

  return {
    modalOpen,
    isNavigatingAway,
    isSaving,
    handleSaveAndLeave,
    handleLeaveWithout,
    handleStay,
  };
}
